/**
 * One-GL-per-PL may not be enforced by the backend; if several leaf lines claim a GL,
 * the first owner (by key order) wins so the label is deterministic.
 *
 * @param {Object} productLinesById persisted product lines keyed by id
 * @param {Object} modifiedProductLinesById staged overlay keyed by id
 * @param {Function} isUnmappedGl predicate (gls) => boolean
 * @returns {{ mappedGlIds: Set<number>, ownerByGlId: Map<number, {id: string, name: string}> }}
 */
export const buildMappedGlData = (productLinesById, modifiedProductLinesById, isUnmappedGl) => {
  const mappedGlIds = new Set();
  const ownerByGlId = new Map();
  const overlay = modifiedProductLinesById || {};

  for (const id of Object.keys(productLinesById || {})) {
    const productLine = overlay[id] || productLinesById[id];
    if (!productLine || !productLine.isLeaf || isUnmappedGl(productLine.gls)) {
      continue;
    }

    for (const glId of productLine.gls) {
      // -1 is the Unmapped sentinel, not a real GL.
      if (glId !== -1 && glId != null) {
        mappedGlIds.add(glId);
        if (!ownerByGlId.has(glId)) {
          ownerByGlId.set(glId, { id: productLine.id, name: productLine.name });
        }
      }
    }
  }

  return { mappedGlIds, ownerByGlId };
};

/**
 * GLs mapped to another PL stay visible but disabled, tagged with the owning PL id
 * (mappedToId) so the dropdown can badge them, rather than hidden. Relies on
 * react-select 2.3.0's default isOptionDisabled reading `isDisabled`.
 *
 * @param {Array<{label: string, value: string}>} productGroupOptions all options
 * @param {Array<number>} glIds the ids this row currently holds
 * @param {Set<number>} mappedGlIds ids mapped to some leaf line (incl. this row)
 * @param {Map<number, {id: string, name: string}>} ownerByGlId mapped id -> owner
 * @returns {Array} options, with cross-mapped ones marked { isDisabled, mappedToId, mappedToName }
 */
export const buildProductGroupOptions = (productGroupOptions, glIds, mappedGlIds, ownerByGlId) => {
  return productGroupOptions.map((option) => {
    const glId = parseInt(option.value);

    // Self-row exemption: without it this row's current selection silently disappears.
    if (glIds.includes(glId)) {
      return option;
    }

    if (!mappedGlIds.has(glId)) {
      return option;
    }

    // Keep the plain label; the owner id is rendered as a menu-only Badge by the
    // dropdown (a string label cannot carry a React element).
    const owner = ownerByGlId && ownerByGlId.get(glId);
    return { ...option, isDisabled: true, mappedToId: owner && owner.id, mappedToName: owner && owner.name };
  });
};



// WEBPACK FOOTER //
// ./src/components/config_central/pl_gl_mapping/option_builder.js